import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ChevronDown, Search } from 'lucide-react'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'

interface TimeZonePickerProps {
  id: string
  value: string
  timeZones: string[]
  onChange: (timeZoneId: string) => void
  onBlur?: () => void
  invalid?: boolean
  'aria-describedby'?: string
}

const SEARCH_PLACEHOLDER = 'Search time zones'

export const TimeZonePicker = forwardRef<HTMLButtonElement, TimeZonePickerProps>(function TimeZonePicker(
  { id, value, timeZones, onChange, onBlur, invalid, 'aria-describedby': describedBy },
  ref,
) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selectedRef = useRef<HTMLDivElement>(null)

  const options = useMemo(
    () => (value && !timeZones.includes(value) ? [value, ...timeZones] : timeZones),
    [timeZones, value],
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return options
    return options.filter(zone => zone.toLowerCase().replace(/_/g, ' ').includes(term.replace(/_/g, ' ')))
  }, [options, search])

  useEffect(() => {
    if (open) selectedRef.current?.scrollIntoView({ block: 'center' })
  }, [open])

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setSearch('')
      onBlur?.()
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          ref={ref}
          type="button"
          id={id}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className="flex h-10 w-full items-center justify-between rounded-md border border-border bg-input-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>{value || 'Select a time zone'}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
        <div className="p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              placeholder={SEARCH_PLACEHOLDER}
              aria-label={SEARCH_PLACEHOLDER}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8"
              onKeyDown={e => e.stopPropagation()}
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto" data-testid="time-zone-options">
          {filtered.length > 0 ? (
            <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
              {filtered.map(zone => (
                <DropdownMenuRadioItem key={zone} value={zone} ref={zone === value ? selectedRef : undefined}>
                  {zone}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          ) : (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">No time zones found</div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
})
